import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { FormDialog } from '@/components/forms/FormDialog';
import { FormSection } from '@/components/forms/Form';
import { FormField, FormSelect, FormTextarea } from '@/components/forms/FormField';
import { useFormateurs, useUpsertFormation } from '@/hooks/queries/useFormation';
import { useAllSousSegments } from '@/hooks/queries/usePerimetres';
import type {
  FormationModalite,
  FormationNiveau,
  FormationRow,
  ParentSegmentType,
} from '@/lib/database.types';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  /** Entreprise propriétaire de la formation (EURL Kost Group par défaut) */
  entrepriseId: string;
  /** Sous-segment pré-sélectionné depuis le contexte de la page (B2B/B2C/CBTA) */
  defaultParentSegment?: ParentSegmentType;
  editing: FormationRow | null;
  onSaved: () => void;
}

const MODALITES: FormationModalite[] = ['presentiel', 'distanciel', 'blended'];
const NIVEAUX: FormationNiveau[] = ['debutant', 'intermediaire', 'avance', 'expert'];

export function FormationFormDialog({
  open,
  onOpenChange,
  entrepriseId,
  defaultParentSegment,
  editing,
  onSaved,
}: Props): JSX.Element {
  const upsert = useUpsertFormation();
  const { data: sousSegments = [] } = useAllSousSegments();
  const { data: formateurs = [] } = useFormateurs();

  const [sousSegmentId, setSousSegmentId] = useState('');
  const [code, setCode] = useState('');
  const [titre, setTitre] = useState('');
  const [description, setDescription] = useState('');
  const [objectifs, setObjectifs] = useState('');
  const [prerequis, setPrerequis] = useState('');
  const [niveau, setNiveau] = useState<FormationNiveau>('debutant');
  const [dureeHeures, setDureeHeures] = useState('');
  const [dureeJours, setDureeJours] = useState('');
  const [modalites, setModalites] = useState<FormationModalite[]>(['presentiel']);
  const [prixInter, setPrixInter] = useState('');
  const [prixIntra, setPrixIntra] = useState('');
  const [prixB2c, setPrixB2c] = useState('');
  const [certifDelivree, setCertifDelivree] = useState(false);
  const [codeCertif, setCodeCertif] = useState('');
  const [validiteMois, setValiditeMois] = useState('');
  const [formateurId, setFormateurId] = useState('');
  const [estPartage, setEstPartage] = useState(true);
  const [statut, setStatut] = useState<'actif' | 'draft' | 'archive'>('actif');
  const [error, setError] = useState<string | null>(null);

  // Sous-segments filtrés par parent_segment courant
  const sousSegmentsFiltrees = useMemo(() => {
    if (!defaultParentSegment) return sousSegments;
    return sousSegments.filter((s) => s.parent_segment === defaultParentSegment);
  }, [sousSegments, defaultParentSegment]);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setSousSegmentId(editing.sous_segment_id ?? '');
      setCode(editing.code);
      setTitre(editing.titre);
      setDescription(editing.description ?? '');
      setObjectifs(editing.objectifs ?? '');
      setPrerequis(editing.prerequis ?? '');
      setNiveau(editing.niveau);
      setDureeHeures(editing.duree_heures ? String(editing.duree_heures) : '');
      setDureeJours(editing.duree_jours ? String(editing.duree_jours) : '');
      setModalites(editing.modalites);
      setPrixInter(editing.prix_inter_unitaire ? String(editing.prix_inter_unitaire) : '');
      setPrixIntra(editing.prix_intra_forfait ? String(editing.prix_intra_forfait) : '');
      setPrixB2c(editing.prix_b2c ? String(editing.prix_b2c) : '');
      setCertifDelivree(editing.certif_delivree);
      setCodeCertif(editing.code_certif ?? '');
      setValiditeMois(editing.validite_mois ? String(editing.validite_mois) : '');
      setFormateurId(editing.formateur_principal_id ?? '');
      setEstPartage(editing.est_partage_b2b);
      setStatut(editing.statut);
    } else {
      setSousSegmentId(sousSegmentsFiltrees[0]?.id ?? '');
      setCode('');
      setTitre('');
      setDescription('');
      setObjectifs('');
      setPrerequis('');
      setNiveau('debutant');
      setDureeHeures('');
      setDureeJours('');
      setModalites(['presentiel']);
      setPrixInter('');
      setPrixIntra('');
      setPrixB2c('');
      setCertifDelivree(false);
      setCodeCertif('');
      setValiditeMois('');
      setFormateurId('');
      // B2B partagé par défaut, B2C non
      setEstPartage(defaultParentSegment !== 'B2C');
      setStatut('actif');
    }
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing, defaultParentSegment, sousSegmentsFiltrees.length]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!code.trim()) return setError('Code obligatoire');
    if (!titre.trim()) return setError('Titre obligatoire');
    if (!sousSegmentId) return setError('Sous-segment obligatoire');
    const inter = prixInter ? Number(prixInter) : null;
    const intra = prixIntra ? Number(prixIntra) : null;
    const b2c = prixB2c ? Number(prixB2c) : null;
    if ((inter ?? 0) <= 0 && (intra ?? 0) <= 0 && (b2c ?? 0) <= 0)
      return setError('Au moins un prix > 0 (inter / intra / B2C)');

    try {
      await upsert.mutateAsync({
        id: editing?.id,
        entreprise_id: entrepriseId,
        sous_segment_id: sousSegmentId || null,
        code: code.trim(),
        titre: titre.trim(),
        description: description || null,
        objectifs: objectifs || null,
        prerequis: prerequis || null,
        niveau,
        duree_heures: dureeHeures ? Number(dureeHeures) : null,
        duree_jours: dureeJours ? Number(dureeJours) : null,
        modalites,
        prix_inter_unitaire: inter,
        prix_intra_forfait: intra,
        prix_b2c: b2c,
        certif_delivree: certifDelivree,
        code_certif: codeCertif || null,
        validite_mois: validiteMois ? Number(validiteMois) : null,
        formateur_principal_id: formateurId || null,
        est_partage_b2b: estPartage,
        statut,
      });
      toast.success(editing ? 'Formation mise à jour' : 'Formation créée');
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    }
  };

  const toggleModalite = (m: FormationModalite): void => {
    setModalites((cur) => (cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m]));
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? `Formation ${editing.code}` : 'Nouvelle formation'}
      description="Création d'une fiche formation au catalogue. Une formation peut être déclinée en plusieurs sessions inter / intra / B2C."
      onSubmit={handleSubmit}
      isSubmitting={upsert.isPending}
      size="xl"
    >
      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {error}
        </div>
      )}

      <FormSection title="Identification">
        <FormSelect
          label="Sous-segment"
          value={sousSegmentId}
          onChange={setSousSegmentId}
          required
          options={[
            { value: '', label: '— Sélectionner —' },
            ...sousSegmentsFiltrees.map((s) => ({ value: s.id, label: s.nom })),
          ]}
        />
        <FormField
          label="Code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          required
          placeholder="ex: FIN-001 / CBTA-7.1 / B2C-COIFF-001"
        />
        <FormField
          label="Titre"
          value={titre}
          onChange={(e) => setTitre(e.target.value)}
          required
          fullWidth
        />
        <FormSelect
          label="Niveau"
          value={niveau}
          onChange={(v) => setNiveau(v as FormationNiveau)}
          options={NIVEAUX.map((n) => ({ value: n, label: n }))}
        />
        <FormSelect
          label="Statut"
          value={statut}
          onChange={(v) => setStatut(v as typeof statut)}
          options={[
            { value: 'actif', label: 'Actif' },
            { value: 'draft', label: 'Brouillon' },
            { value: 'archive', label: 'Archivé' },
          ]}
        />
      </FormSection>

      <FormSection title="Contenu pédagogique">
        <FormTextarea
          label="Description courte"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          rows={2}
        />
        <FormTextarea
          label="Objectifs pédagogiques"
          value={objectifs}
          onChange={(e) => setObjectifs(e.target.value)}
          fullWidth
          rows={3}
        />
        <FormTextarea
          label="Prérequis"
          value={prerequis}
          onChange={(e) => setPrerequis(e.target.value)}
          fullWidth
          rows={2}
        />
      </FormSection>

      <FormSection title="Durée & modalités">
        <FormField
          label="Durée (heures)"
          type="number"
          step="0.5"
          min="0"
          value={dureeHeures}
          onChange={(e) => setDureeHeures(e.target.value)}
        />
        <FormField
          label="Durée (jours)"
          type="number"
          step="0.5"
          min="0"
          value={dureeJours}
          onChange={(e) => setDureeJours(e.target.value)}
        />
        <div className="space-y-1.5 sm:col-span-2">
          <label className="text-xs text-muted-foreground">Modalités</label>
          <div className="flex flex-wrap gap-1">
            {MODALITES.map((m) => {
              const active = modalites.includes(m);
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => toggleModalite(m)}
                  className={
                    active
                      ? 'rounded-md border border-primary bg-primary/10 px-2 py-1 text-xs text-primary'
                      : 'rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-secondary'
                  }
                >
                  {m}
                </button>
              );
            })}
          </div>
        </div>
      </FormSection>

      <FormSection title="Tarification (DZD)">
        <FormField
          label="Prix B2B inter (par participant)"
          type="number"
          step="0.01"
          min="0"
          value={prixInter}
          onChange={(e) => setPrixInter(e.target.value)}
        />
        <FormField
          label="Prix B2B intra (forfait session)"
          type="number"
          step="0.01"
          min="0"
          value={prixIntra}
          onChange={(e) => setPrixIntra(e.target.value)}
        />
        <FormField
          label="Prix B2C (par participant)"
          type="number"
          step="0.01"
          min="0"
          value={prixB2c}
          onChange={(e) => setPrixB2c(e.target.value)}
        />
        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={estPartage}
            onChange={(e) => setEstPartage(e.target.checked)}
          />
          Formation partagée inter-entreprises (visible aux commerciaux B2B des filiales)
        </label>
      </FormSection>

      <FormSection title="Certification">
        <label className="flex items-center gap-2 text-xs sm:col-span-2">
          <input
            type="checkbox"
            checked={certifDelivree}
            onChange={(e) => setCertifDelivree(e.target.checked)}
          />
          Cette formation délivre une certification
        </label>
        {certifDelivree && (
          <>
            <FormField
              label="Code certification"
              value={codeCertif}
              onChange={(e) => setCodeCertif(e.target.value)}
              placeholder="ex: IATA-CBTA-7.1"
            />
            <FormField
              label="Validité (mois) — recyclage requis"
              type="number"
              step="1"
              min="0"
              value={validiteMois}
              onChange={(e) => setValiditeMois(e.target.value)}
              hint="Vide = pas de recyclage. Ex: 24 pour CBTA."
            />
          </>
        )}
      </FormSection>

      <FormSection title="Formateur">
        <FormSelect
          label="Formateur principal"
          value={formateurId}
          onChange={setFormateurId}
          fullWidth
          options={[
            { value: '', label: '— À assigner —' },
            ...formateurs
              .filter((f) => f.is_active)
              .map((f) => ({ value: f.id, label: `${f.prenom} ${f.nom} (${f.statut})` })),
          ]}
        />
      </FormSection>
    </FormDialog>
  );
}
